// Explore Tab Container
//
// Pinterest-style staggered grid of real profile photos, from /feed/explore.
// Previously this generated picsum.photos placeholders for eight invented users
// with fabricated "date night" captions.
//
// Explore casts a deliberately wider net than the matching deck: it keeps the
// gender-preference gate but drops the age, distance and persona narrowing, and
// actively boosts far-away, under-exposed and newly-joined accounts
// (get_explore_feed, migration 043). The point is discovering people the deck
// would never show you.

import React, { useState, useEffect, useRef, useCallback } from "react";
import { ImageCard } from "../FeedItems/ImageCard";
import { ImageModal } from "./ImageModal";
import { exploreService, ExploreImage, newExploreSeed } from "../../../services/SocialFeed/exploreService";
import {
  Looped,
  LoopCursor,
  startLoop,
  tagCycle,
  appendLooped,
  advanceCursor,
  poolIsEmpty,
} from "../../../services/SocialFeed/feedLoop";
import { feedCacheKey, readFeedCache, writeFeedCache } from "../../../services/SocialFeed/feedCache";
import { logger } from "../../../utils/logger";
import { ProfilePeekDrawer, useProfilePeek } from "../../shared/ProfilePeekDrawer";
import { signalsService } from "../../../services/SocialFeed/signalsService";
import {
  ExploreContainer,
  ExploreMasonryGrid,
  ExploreMasonryColumns,
  LoadingContainer,
  LoadingSpinner,
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateDescription,
} from "../../../styles/SocialFeed/ExploreTab.styles";
import { FeedToast } from "../../../styles/SocialFeed/MatchesTab.styles";

interface ExploreTabContainerProps {
  userId: string;
  /**
   * Optional escape hatch for an embedder that genuinely wants navigation.
   * Left unset by the feed, which is what makes the profile open in the drawer
   * over the grid — navigating away costs the viewer their scroll position in
   * a feed whose whole purpose is browsing.
   */
  onViewProfile?: (userId: string) => void;
}

const PAGE_SIZE = 40;

export const ExploreTabContainer: React.FC<ExploreTabContainerProps> = ({
  userId,
  onViewProfile,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { peekTarget, openPeek, closePeek, toast, showToast } = useProfilePeek('explore');
  // Whatever this tab last held, if it is still fresh. Read once, at mount:
  // re-reading on later renders would swap content under a viewer mid-scroll.
  const [cached] = useState(() =>
    readFeedCache<Looped<ExploreImage>>(feedCacheKey('explore', userId))
  );

  const [images, setImages] = useState<Looped<ExploreImage>[]>(cached?.items ?? []);
  // A cache hit means there is nothing to wait for, so no spinner.
  const [isLoading, setIsLoading] = useState(!cached);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [selectedImage, setSelectedImage] = useState<ExploreImage | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [savedScrollPosition, setSavedScrollPosition] = useState(0);

  // Offset, lap and seed for the session. The backend's ordering is
  // deterministic per seed, so paging by offset only works while the seed
  // stays fixed — a fresh seed mid-pass would reshuffle the grid and make
  // tiles repeat or vanish under the scroll. The seed therefore changes at
  // exactly one moment: when a pass ends and the next one begins.
  // Restored alongside the items. Without it a returning viewer would render
  // the cached tiles and then page from offset zero, serving the first forty
  // people twice.
  const cursorRef = useRef<LoopCursor>(cached?.cursor ?? startLoop(newExploreSeed()));
  // Guards against a scroll event firing another page request while one is
  // already in flight, which is how duplicate tiles get appended.
  const isFetchingRef = useRef(false);

  const loadPage = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setIsLoading(true);

    const cursor = cursorRef.current;

    try {
      const page = await exploreService.getExploreFeed(PAGE_SIZE, cursor.offset, cursor.seed);
      setLoadError(null);

      // A short page means this pass is spent, so the cursor wraps to the top
      // with a new seed rather than stopping. Explore is finite; the scroll
      // through it is not.
      cursorRef.current = advanceCursor(cursor, page.length, PAGE_SIZE, newExploreSeed);

      // The one thing that does stop the loop: a pass that started at the top
      // and found nobody. Wrapping an empty pool would request page 0 forever.
      if (poolIsEmpty(cursor, page.length)) setHasMore(false);

      setImages((prev) => appendLooped(prev, tagCycle(page, cursor.cycle)));
    } catch (error: any) {
      logger.error("[EXPLORE] Failed to load explore feed:", error);
      // Surface the real reason rather than a generic string.
      setLoadError(error?.message || "Explore could not be loaded. Try again shortly.");
      setHasMore(false);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    signalsService.track({ signalType: 'feed_open', surface: 'explore' });
    // Already warm, either from a previous visit or from the prefetcher.
    if (!cached) loadPage();
  }, [loadPage, userId]);

  // Hand the loaded grid back for the next visit. Keyed on images so it also
  // captures pages appended by scrolling, not just the first one.
  useEffect(() => {
    if (images.length) {
      writeFeedCache(feedCacheKey('explore', userId), {
        items: images,
        cursor: cursorRef.current,
      });
    }
  }, [images, userId]);

  // Opens the profile over the grid rather than navigating, matching the
  // Matches and Nearby feeds. Closing the image modal first keeps the two
  // overlays from stacking, since a tile's profile icon is reachable from
  // inside the modal as well as from the grid.
  const handleViewProfile = useCallback(
    (profileUserId: string) => {
      if (onViewProfile) {
        onViewProfile(profileUserId);
        return;
      }
      const tile = images.find((img) => img.userId === profileUserId);
      setIsModalOpen(false);
      openPeek({
        profileId: profileUserId,
        firstName: tile?.username || 'Someone',
        // Explore's context is where they are and whether they are new, since
        // it deliberately reaches past the people the deck would show.
        context: tile?.isNewMember
          ? 'New here'
          : tile?.location
            ? `From ${tile.location}`
            : 'From Explore',
      });
    },
    [images, onViewProfile, openPeek]
  );

  // Takes just the id so the signature stays compatible with ImageCard's
  // callback, then resolves the full tile from state — the card is typed for the
  // fields it renders, which is a subset of what the modal wants.
  const handleImageClick = useCallback((clicked: { id: string }) => {
    if (scrollContainerRef.current) {
      setSavedScrollPosition(scrollContainerRef.current.scrollTop);
    }
    const tile = images.find((img) => img.id === clicked.id) ?? null;
    setSelectedImage(tile);
    setIsModalOpen(true);
    if (tile) {
      // Opening someone's photo full-size in a browsing grid is a deliberate
      // act of interest, distinct from the tile merely scrolling past.
      signalsService.track({
        signalType: 'explore_tile_open',
        targetUserId: tile.userId,
        surface: 'explore',
        itemId: tile.id,
      });
    }
  }, [images]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    // Restore scroll position after a brief delay to ensure modal is closed
    setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = savedScrollPosition;
      }
    }, 100);
  }, [savedScrollPosition]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      const scrollPercentage = (scrollHeight - clientHeight) > 0
        ? scrollTop / (scrollHeight - clientHeight)
        : 0;

      if (scrollPercentage > 0.8 && hasMore && !isFetchingRef.current) {
        // How deep the session actually got, recorded as the page is requested
        // rather than on unmount, where a discarded tab would lose it.
        signalsService.track({
          signalType: 'feed_scroll_depth',
          surface: 'explore',
          metadata: { tilesSeen: images.length },
        });
        loadPage();
      }
    },
    [hasMore, images.length, loadPage]
  );

  if (isLoading && images.length === 0) {
    return (
      <ExploreContainer>
        <LoadingContainer>
          <LoadingSpinner />
          <p>Loading images...</p>
        </LoadingContainer>
      </ExploreContainer>
    );
  }

  if (loadError && images.length === 0) {
    return (
      <ExploreContainer>
        <EmptyState>
          <EmptyStateIcon>⚠️</EmptyStateIcon>
          <EmptyStateTitle>Couldn't load Explore</EmptyStateTitle>
          <EmptyStateDescription>{loadError}</EmptyStateDescription>
        </EmptyState>
      </ExploreContainer>
    );
  }

  if (images.length === 0) {
    return (
      <ExploreContainer>
        <EmptyState>
          <EmptyStateIcon>📸</EmptyStateIcon>
          <EmptyStateTitle>No images to explore</EmptyStateTitle>
          <EmptyStateDescription>
            Check back later for new user uploads.
          </EmptyStateDescription>
        </EmptyState>
      </ExploreContainer>
    );
  }

  return (
    <ExploreContainer>
      <ExploreMasonryGrid
        ref={scrollContainerRef}
        onScroll={handleScroll}
      >
        <ExploreMasonryColumns>
          {images.map((image) => (
            <ImageCard
              key={image.loopKey}
              image={image}
              onViewProfile={handleViewProfile}
              onImageClick={handleImageClick}
            />
          ))}
        </ExploreMasonryColumns>
        {/* Outside the columns on purpose: inside, the spinner becomes a
            masonry item and gets dealt into a column mid-grid. */}
        {isLoading && images.length > 0 && (
          <LoadingContainer>
            <LoadingSpinner />
            <p>Loading more...</p>
          </LoadingContainer>
        )}
      </ExploreMasonryGrid>
      <ImageModal
        isOpen={isModalOpen}
        image={selectedImage}
        onClose={handleCloseModal}
        onViewProfile={handleViewProfile}
      />

      {toast && <FeedToast role="status">{toast}</FeedToast>}

      {/* Profile preview — the same drawer Matches and Nearby open, so the
          profile icon behaves identically across every feed. */}
      <ProfilePeekDrawer
        target={peekTarget}
        onClose={closePeek}
        backLabel="Explore"
        onNotify={(_type, message) => showToast(message)}
      />
    </ExploreContainer>
  );
};
